import styles from '../../styles/components/pst/location-form-field.module.scss';
import InputAndLabel from '../base/input-and-label';
import SelectAndLabel from '../base/select-and-label';
import RadioAndLabel from '../base/radio-and-label';
import { useContext, useEffect, useReducer, useState } from 'react';
import { GeneralConfigContext } from '../general-config-provider';
import {
  getDepartmentMunicipalities,
  getDepartmentName,
  getMunicipalityDepartmentID,
  getMunicipalityName,
} from '../../shared/utils';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import axios from 'axios';
import { GOOGLE_MAPS_API_KEY } from '../../shared/constants';
import { useTranslations } from 'next-intl';

export default function LocationFormField(props) {
  const { generalConfig } = useContext(GeneralConfigContext);
  const t = useTranslations('viewOffer');
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const hideAddress = props.hideAddress ?? false;

  const viewOnly = props.viewOnly ?? false;
  const viewOnlySede = props.viewOnlySede ?? false;

  const [departmentSelected, setDepartmentSelected] = useState(null);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [locationCoordinates, setLocationCoordinates] = useState(null);
  const [coordinatesNotFound, setCoordinatesNotFound] = useState(false);
  const [active, setActive] = useState(false);

  const [formFields, setFormFields] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {}
  );

  const handleFormChange = (e) => {
    setFormFields({ [e.target.name]: e.target.value });
  };

  const handleAddressBlur = async () => {
    if (coordinatesNotFound) setCoordinatesNotFound(false);

    if (formFields.id_municipio) {
      setLocationCoordinates(
        await getAddressCoordinates(
          formFields.id_municipio,
          formFields.direccion
        )
      );
    }
  };

  const getAddressCoordinates = async (municipalityID, address) => {
    try {
      const geocodeResult = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )},${getMunicipalityName(
          municipalityID,
          generalConfig.ciudad
        )},Colombia&key=${GOOGLE_MAPS_API_KEY}`
      );

      if (geocodeResult.data.results.length === 0) {
        setCoordinatesNotFound(true);
        return null;
      }

      if (coordinatesNotFound && geocodeResult.data.results.length > 0) {
        setCoordinatesNotFound(false);
      }

      const coordinates = geocodeResult.data.results[0]?.geometry.location;

      setFormFields({ coordenadas: `${coordinates.lat},${coordinates.lng}` });
      if (
        (props.idUbication == null && props.valueCategory == 0) ||
        !props.ubication
      ) {
        props.onFormSaved(formFields);
      }
      return coordinates;
    } catch (error) {
      return Promise.resolve(null);
    }
  };

  const printGoogleMap = () => {
    return (
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={locationCoordinates}
        zoom={18}
      >
        <Marker position={locationCoordinates} />
      </GoogleMap>
    );
  };

  const disableUbication = (value) => {
    if (value !== null) {
      return value;
    }
    return false;
  };

  useEffect(() => {
    (async () => {
      if (generalConfig && props.value) {
        setDepartmentSelected(
          getMunicipalityDepartmentID(
            props.value.id_municipio,
            generalConfig?.ciudad
          )
        );

        setFormFields({ id_municipio: props.value.id_municipio });
        setFormFields({ direccion: props.value.direccion });

        if (
          props.value.coordenadas !== null &&
          props.value.coordenadas !== undefined
        ) {
          const coordinatesParts = props.value.coordenadas.split(',');
          setLocationCoordinates({
            lat: Number(coordinatesParts[0]),
            lng: Number(coordinatesParts[1]),
          });
          if (formFields?.id_municipio || formFields?.direccion) {
            props.onFormSaved(formFields);
          }
        } else {
          if (props.value.id_municipio && props.value.direccion) {
            setLocationCoordinates(
              await getAddressCoordinates(
                props.value.id_municipio,
                props.value.direccion
              )
            );
          }
        }
      }
    })();

    if (props?.value?.direccion === '') {
      setActive(false);
    } else {
      setActive(true);
    }
  }, [generalConfig, props?.value]);

  useEffect(() => {
    if (generalConfig && departmentSelected) {
      if (props.municipiosPrioriced === undefined) {
        setMunicipalityOptions(
          getDepartmentMunicipalities(departmentSelected, generalConfig.ciudad)
        );
      } else {
        setMunicipalityOptions(
          getDepartmentMunicipalities(
            departmentSelected,
            generalConfig.ciudad
          ).filter((d) => d.PST == 1)
        );
      }
    }
  }, [generalConfig, departmentSelected]);

  useEffect(() => {
    if (props.onChange) {
      props.onChange(formFields);
    }
  }, [formFields]);

  return (
    <div className={styles.locationFormField}>
      {!viewOnly && (
        <div className={styles.formFields}>
          <RadioAndLabel
            label="Departamento"
            name="Departamentos"
            showRequiredMark={true}
            layout="grid"
            options={generalConfig?.departamento.map((department) => {
              return {
                name: department.nombre,
                value: department.id_departamento,
              };
            })}
            disable={disableUbication(props.disable)}
            onChange={(event) => setDepartmentSelected(event.target.value)}
            value={departmentSelected}
          />

          <SelectAndLabel
            label="Municipio"
            options={municipalityOptions}
            showRequiredMark={true}
            name="id_municipio"
            disable={disableUbication(props.disable)}
            onChange={(e) => handleFormChange(e)}
            value={formFields['id_municipio']}
          />

          {!hideAddress && (
            <InputAndLabel
              label="Dirección"
              icon="location"
              name="direccion"
              onBlur={handleAddressBlur}
              onChange={(e) => handleFormChange(e)}
              disable={disableUbication(props.disable)}
              showRequiredMark={true}
              value={formFields['direccion']}
            />
          )}
        </div>
      )}

      {viewOnly && !viewOnlySede && (
        <div className={styles.onlyViewBox}>
          <p>{t('ubicacion')}</p>
          <p>
            {getDepartmentName(
              getMunicipalityDepartmentID(
                props.value.id_municipio,
                generalConfig?.ciudad
              ),
              generalConfig?.departamento
            )}
            ,{' '}
            {getMunicipalityName(
              props.value.id_municipio,
              generalConfig?.ciudad
            )}
          </p>
          <p>{props.value.direccion}</p>
        </div>
      )}

      {isLoaded && locationCoordinates && active && (
        <div className={styles.mapContainer}>{printGoogleMap()}</div>
      )}

      {coordinatesNotFound && <p>{t('notFoundCordenadas')}</p>}
    </div>
  );
}
